<script setup lang="ts">
import { ref, computed } from 'vue';
import { useScenarioStore } from '../store/scenario';
import NumberInput from './NumberInput.vue';
import SelectInput from './SelectInput.vue';
import ToggleRow from './ToggleRow.vue';
import TooltipIcon from './TooltipIcon.vue';
import { ScenarioInputs } from '../lib/types';
import { encodeScenario, decodeScenario } from '../lib/encode';

const store = useScenarioStore();
const scenario = computed(() => store.scenario);
const importText = ref('');
const importError = ref('');

const currencyOptions = [
  { value: 'PLN', label: 'PLN' },
  { value: 'EUR', label: 'EUR' }
];

const loanCurrencyOptions = currencyOptions;

const inflationModeOptions = [
  { value: 'real', label: 'Real (constant PLN, real return)' },
  { value: 'nominal', label: 'Nominal (indexed by inflation)' }
];

const endExitOptions = [
  { value: 'FULL_EXIT_ALWAYS', label: 'Full exit at horizon (retirement mode)' },
  { value: 'manual', label: 'Manual toggle (advanced)' }
];

const rebalanceOptions = [
  { value: 'annual', label: 'Annual' },
  { value: 'never', label: 'Never' }
];

const citOptions = [
  { value: 'classic', label: 'Classic CIT' },
  { value: 'estonian', label: 'Estonian CIT' }
];

function updateSection<K extends keyof ScenarioInputs, F extends keyof ScenarioInputs[K]>(
  key: K,
  field: F,
  value: ScenarioInputs[K][F]
) {
  const updated = { ...scenario.value[key], [field]: value };
  store.update(key, updated as ScenarioInputs[K]);
}

function exportScenario() {
  const json = JSON.stringify(scenario.value, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'scenario.json';
  anchor.click();
  URL.revokeObjectURL(url);
}

function importScenario() {
  try {
    const parsed = JSON.parse(importText.value);
    store.setScenario(parsed);
    importError.value = '';
  } catch (err) {
    importError.value = 'Invalid JSON payload';
  }
}

function copyShareLink() {
  const encoded = encodeScenario(scenario.value);
  const link = `${window.location.origin}${window.location.pathname}#${encoded}`;
  navigator.clipboard.writeText(link);
}

function loadFromHash() {
  const hash = window.location.hash.replace('#', '');
  if (!hash) return;
  const decoded = decodeScenario<ScenarioInputs>(hash);
  if (decoded) {
    store.setScenario(decoded);
  }
}
</script>

<template>
  <div class="card">
    <div class="toolbar" style="margin-bottom: 0.75rem">
      <button class="primary" @click="store.reset()">Reset to defaults</button>
      <button class="secondary" @click="exportScenario">Export JSON</button>
      <button class="secondary" @click="copyShareLink">Copy shareable link</button>
      <button class="secondary" @click="loadFromHash">Load from URL hash</button>
    </div>

    <details open>
      <summary class="section-title">A) General</summary>
      <div class="grid">
        <NumberInput
          label="Horizon (years)"
          :model-value="scenario.general.horizonYears"
          :min="1"
          @update:model-value="val => updateSection('general', 'horizonYears', val)"
          tooltip="Length of the simulation horizon."
        />
        <NumberInput
          label="Annual return assumption %"
          :model-value="scenario.general.annualReturnPct"
          :step="0.1"
          @update:model-value="val => updateSection('general', 'annualReturnPct', val)"
          tooltip="Nominal annual portfolio return, applied monthly."
        />
        <NumberInput
          label="Inflation %"
          :model-value="scenario.general.inflationPct"
          :step="0.1"
          @update:model-value="val => updateSection('general', 'inflationPct', val)"
          tooltip="Inflation assumption used for nominal/real toggles."
        />
        <SelectInput
          label="Simulation mode"
          :model-value="scenario.general.inflationMode || 'real'"
          :options="inflationModeOptions"
          @update:model-value="val => updateSection('general', 'inflationMode', val as any)"
          tooltip="Nominal indexes income/life-need by inflation and leaves return nominal; Real holds income/life-need flat and converts returns to real."
        />
        <ToggleRow
          label="Retirement mode (force full exit)"
          :model-value="scenario.general.retirementMode"
          @update:model-value="val => updateSection('general', 'retirementMode', val)"
          tooltip="When enabled, the model always liquidates both portfolios at horizon and distributes cash to the owner."
        />
        <SelectInput
          label="End-of-horizon handling"
          :model-value="scenario.general.endExitMode"
          :options="endExitOptions"
          :disabled="scenario.general.retirementMode"
          @update:model-value="val => updateSection('general', 'endExitMode', val as any)"
          tooltip="Full exit is enforced for retirement comparisons. Manual is available for advanced what-if scenarios."
        />
      </div>
    </details>

    <details open>
      <summary class="section-title">B) Income & Life</summary>
      <div class="grid">
        <SelectInput
          label="Income currency"
          :model-value="scenario.income.incomeCurrency"
          :options="currencyOptions"
          @update:model-value="val => updateSection('income', 'incomeCurrency', val as any)"
          tooltip="Currency of recurring income."
        />
        <NumberInput
          label="Monthly income"
          :model-value="scenario.income.monthlyIncome"
          @update:model-value="val => updateSection('income', 'monthlyIncome', val)"
          tooltip="Gross inflow to JDG or sp. z o.o."
        />
        <NumberInput
          label="FX rate EUR→PLN"
          :model-value="scenario.income.fxRate"
          :step="0.01"
          @update:model-value="val => updateSection('income', 'fxRate', val)"
          tooltip="Used when income currency is EUR."
        />
        <NumberInput
          label="Life-need monthly (PLN)"
          :model-value="scenario.income.lifeNeedMonthly"
          @update:model-value="val => updateSection('income', 'lifeNeedMonthly', val)"
          tooltip="Target personal cash need per month."
        />
        <NumberInput
          label="Annual leakage %"
          :model-value="scenario.income.leakagePct"
          :step="0.1"
          @update:model-value="val => updateSection('income', 'leakagePct', val)"
          tooltip="Assumed drag on revenue (collection loss, slippage)."
        />
        <NumberInput
          label="Safety buffer %"
          :model-value="scenario.income.bufferPct"
          :step="0.1"
          @update:model-value="val => updateSection('income', 'bufferPct', val)"
          tooltip="Retained buffer from investable cash each month."
        />
      </div>
    </details>

    <details>
      <summary class="section-title">C) Bookkeeping & Admin</summary>
      <div class="grid">
        <NumberInput
          label="JDG bookkeeping monthly"
          :model-value="scenario.bookkeeping.jdgBookkeeping"
          @update:model-value="val => updateSection('bookkeeping', 'jdgBookkeeping', val)"
        />
        <NumberInput
          label="sp. z o.o. bookkeeping monthly"
          :model-value="scenario.bookkeeping.spBookkeeping"
          @update:model-value="val => updateSection('bookkeeping', 'spBookkeeping', val)"
        />
        <NumberInput
          label="Virtual office"
          :model-value="scenario.bookkeeping.virtualOffice"
          @update:model-value="val => updateSection('bookkeeping', 'virtualOffice', val)"
        />
        <NumberInput
          label="Other admin"
          :model-value="scenario.bookkeeping.otherAdmin"
          @update:model-value="val => updateSection('bookkeeping', 'otherAdmin', val)"
        />
      </div>
    </details>

    <details>
      <summary class="section-title">D) JDG model</summary>
      <div class="grid">
        <NumberInput
          label="Ryczałt %"
          :model-value="scenario.jdg.ryczaltPct"
          :step="0.1"
          @update:model-value="val => updateSection('jdg', 'ryczaltPct', val)"
        />
        <ToggleRow
          label="ZUS as % of revenue"
          :model-value="scenario.jdg.zusAsPercent"
          @update:model-value="val => updateSection('jdg', 'zusAsPercent', val)"
          tooltip="If enabled, ZUS/health drag is computed as % of revenue; otherwise fixed amount."
        />
        <NumberInput
          label="ZUS %"
          :model-value="scenario.jdg.zusPercent"
          :step="0.1"
          @update:model-value="val => updateSection('jdg', 'zusPercent', val)"
        />
        <NumberInput
          label="ZUS fixed"
          :model-value="scenario.jdg.zusFixed"
          @update:model-value="val => updateSection('jdg', 'zusFixed', val)"
        />
        <NumberInput
          label="Private cap gains tax %"
          :model-value="scenario.jdg.capGainsTaxPct"
          :step="0.1"
          @update:model-value="val => updateSection('jdg', 'capGainsTaxPct', val)"
        />
        <SelectInput
          label="Rebalancing"
          :model-value="scenario.jdg.rebalance"
          :options="rebalanceOptions"
          @update:model-value="val => updateSection('jdg', 'rebalance', val as any)"
          tooltip="Annual realization vs. only on exit."
        />
        <NumberInput
          label="Private leakage %"
          :model-value="scenario.jdg.privateLeakagePct"
          :step="0.1"
          @update:model-value="val => updateSection('jdg', 'privateLeakagePct', val)"
        />
      </div>
    </details>

    <details>
      <summary class="section-title">E) Sp. z o.o. model</summary>
      <div class="grid">
        <SelectInput
          label="CIT mode"
          :model-value="scenario.spzoo.citMode"
          :options="citOptions"
          @update:model-value="val => updateSection('spzoo', 'citMode', val as any)"
        />
        <NumberInput
          label="Classic CIT rate %"
          :model-value="scenario.spzoo.classicCITRatePct"
          :step="0.1"
          @update:model-value="val => updateSection('spzoo', 'classicCITRatePct', val)"
        />
        <ToggleRow
          label="Estonian simple effective rate"
          :model-value="scenario.spzoo.estonian.useSimple"
          @update:model-value="val => updateSection('spzoo', 'estonian', { ...scenario.spzoo.estonian, useSimple: val })"
          tooltip="If disabled, uses detailed estonian CIT + dividend PIT + offset."
        />
        <NumberInput
          label="Estonian effective distribution %"
          :model-value="scenario.spzoo.estonian.effectiveDistributionPct"
          :step="0.1"
          :disabled="!scenario.spzoo.estonian.useSimple || scenario.spzoo.citMode !== 'estonian'"
          @update:model-value="val => updateSection('spzoo', 'estonian', { ...scenario.spzoo.estonian, effectiveDistributionPct: val })"
        />
        <NumberInput
          label="Estonian CIT %"
          :model-value="scenario.spzoo.estonian.detailed.estonianCITRatePct"
          :step="0.1"
          :disabled="scenario.spzoo.estonian.useSimple || scenario.spzoo.citMode !== 'estonian'"
          @update:model-value="val => updateSection('spzoo', 'estonian', { ...scenario.spzoo.estonian, detailed: { ...scenario.spzoo.estonian.detailed, estonianCITRatePct: val } })"
        />
        <NumberInput
          label="Dividend PIT %"
          :model-value="scenario.spzoo.estonian.detailed.dividendPITPct"
          :step="0.1"
          :disabled="scenario.spzoo.estonian.useSimple || scenario.spzoo.citMode !== 'estonian'"
          @update:model-value="val => updateSection('spzoo', 'estonian', { ...scenario.spzoo.estonian, detailed: { ...scenario.spzoo.estonian.detailed, dividendPITPct: val } })"
        />
        <NumberInput
          label="PIT offset %"
          :model-value="scenario.spzoo.estonian.detailed.pitOffsetPct"
          :step="0.1"
          :disabled="scenario.spzoo.estonian.useSimple || scenario.spzoo.citMode !== 'estonian'"
          @update:model-value="val => updateSection('spzoo', 'estonian', { ...scenario.spzoo.estonian, detailed: { ...scenario.spzoo.estonian.detailed, pitOffsetPct: val } })"
        />
        <NumberInput
          label="Company cap gains tax %"
          :model-value="scenario.spzoo.companyCapGainsTaxPct"
          :step="0.1"
          @update:model-value="val => updateSection('spzoo', 'companyCapGainsTaxPct', val)"
        />
        <SelectInput
          label="Company rebalancing"
          :model-value="scenario.spzoo.rebalance"
          :options="rebalanceOptions"
          @update:model-value="val => updateSection('spzoo', 'rebalance', val as any)"
        />
        <NumberInput
          label="Tooling subscriptions"
          :model-value="scenario.spzoo.toolingSubsMonthly"
          @update:model-value="val => updateSection('spzoo', 'toolingSubsMonthly', val)"
        />
        <NumberInput
          label="Dividend tax %"
          :model-value="scenario.spzoo.dividendTaxPct"
          :step="0.1"
          @update:model-value="val => updateSection('spzoo', 'dividendTaxPct', val)"
        />
        <ToggleRow
          label="Keep funds inside company at end"
          :model-value="scenario.spzoo.keepInCompany"
          @update:model-value="val => updateSection('spzoo', 'keepInCompany', val)"
          tooltip="Advanced: only respected when end-of-horizon handling is set to manual. Retirement mode enforces full exit."
        />
      </div>
    </details>

    <details>
      <summary class="section-title">F) Owner cashflow & solver</summary>
      <div class="grid">
        <ToggleRow
          label="Enable solver for salary"
          :model-value="scenario.owner.solverEnabled"
          @update:model-value="val => updateSection('owner', 'solverEnabled', val)"
          tooltip="When on, solves minimal appointment salary to net the life-need."
        />
        <ToggleRow
          label="Allow salary"
          :model-value="scenario.owner.allowSalary"
          @update:model-value="val => updateSection('owner', 'allowSalary', val)"
        />
        <ToggleRow
          label="Allow dividends"
          :model-value="scenario.owner.allowDividends"
          @update:model-value="val => updateSection('owner', 'allowDividends', val)"
        />
        <ToggleRow
          label="Allow shareholder loan"
          :model-value="scenario.owner.allowLoan"
          @update:model-value="val => updateSection('owner', 'allowLoan', val)"
        />
        <NumberInput
          label="Minimum salary gross"
          :model-value="scenario.owner.minSalaryGross"
          @update:model-value="val => updateSection('owner', 'minSalaryGross', val)"
          tooltip="Used as a floor for solver or manual salary."
        />
        <NumberInput
          label="Solver salary portion % of life-need"
          :model-value="scenario.owner.salaryPortionPct"
          :step="1"
          @update:model-value="val => updateSection('owner', 'salaryPortionPct', val)"
          tooltip="When solver is on, percentage of life-need covered by salary."
        />
        <NumberInput
          label="Solver dividend portion % of life-need"
          :model-value="scenario.owner.dividendPortionPct"
          :step="1"
          @update:model-value="val => updateSection('owner', 'dividendPortionPct', val)"
          tooltip="When solver is on, percentage of life-need covered by dividends."
        />
        <NumberInput
          label="Manual salary gross"
          :model-value="scenario.owner.manualSalaryGross"
          @update:model-value="val => updateSection('owner', 'manualSalaryGross', val)"
          tooltip="Used when solver is disabled."
        />
        <NumberInput
          label="Manual dividend gross"
          :model-value="scenario.owner.manualDividendGross"
          @update:model-value="val => updateSection('owner', 'manualDividendGross', val)"
          tooltip="Used when solver is disabled."
        />
        <NumberInput
          label="PIT threshold"
          :model-value="scenario.owner.progressive.threshold"
          @update:model-value="val => updateSection('owner', 'progressive', { ...scenario.owner.progressive, threshold: val })"
          tooltip="Annual PIT threshold; converted to monthly inside the salary solver."
        />
        <NumberInput
          label="PIT lower rate %"
          :model-value="scenario.owner.progressive.lowerRatePct"
          :step="0.1"
          @update:model-value="val => updateSection('owner', 'progressive', { ...scenario.owner.progressive, lowerRatePct: val })"
        />
        <NumberInput
          label="PIT upper rate %"
          :model-value="scenario.owner.progressive.upperRatePct"
          :step="0.1"
          @update:model-value="val => updateSection('owner', 'progressive', { ...scenario.owner.progressive, upperRatePct: val })"
        />
        <NumberInput
          label="Health %"
          :model-value="scenario.owner.progressive.healthPct"
          :step="0.1"
          @update:model-value="val => updateSection('owner', 'progressive', { ...scenario.owner.progressive, healthPct: val })"
        />
        <NumberInput
          label="Dividend tax %"
          :model-value="scenario.owner.dividendTaxPct"
          :step="0.1"
          @update:model-value="val => updateSection('owner', 'dividendTaxPct', val)"
        />
        <NumberInput
          label="Loan interest %"
          :model-value="scenario.owner.loan.interestRatePct"
          :step="0.1"
          @update:model-value="val => updateSection('owner', 'loan', { ...scenario.owner.loan, interestRatePct: val })"
        />
        <NumberInput
          label="Loan interest tax %"
          :model-value="scenario.owner.loan.interestTaxPct"
          :step="0.1"
          @update:model-value="val => updateSection('owner', 'loan', { ...scenario.owner.loan, interestTaxPct: val })"
        />
        <NumberInput
          label="Loan term (years)"
          :model-value="scenario.owner.loan.termYears"
          :step="0.5"
          @update:model-value="val => updateSection('owner', 'loan', { ...scenario.owner.loan, termYears: val })"
        />
        <ToggleRow
          label="Interest-only loan"
          :model-value="scenario.owner.loan.interestOnly"
          @update:model-value="val => updateSection('owner', 'loan', { ...scenario.owner.loan, interestOnly: val })"
        />
        <NumberInput
          label="Loan principal limit"
          :model-value="scenario.owner.loan.principalLimit"
          @update:model-value="val => updateSection('owner', 'loan', { ...scenario.owner.loan, principalLimit: val })"
        />
        <NumberInput
          label="Loan lump-sum amount"
          :model-value="scenario.owner.loan.lumpSumAmount"
          @update:model-value="val => updateSection('owner', 'loan', { ...scenario.owner.loan, lumpSumAmount: val })"
          tooltip="One-time draw amount when the loan triggers."
        />
        <NumberInput
          label="Loan disbursement year"
          :model-value="scenario.owner.loan.lumpSumYear"
          :min="1"
          :step="0.5"
          @update:model-value="val => updateSection('owner', 'loan', { ...scenario.owner.loan, lumpSumYear: val })"
          tooltip="Year of the simulation when the lump-sum loan is taken."
        />
        <SelectInput
          label="Loan currency"
          :model-value="scenario.owner.loan.currency"
          :options="loanCurrencyOptions"
          @update:model-value="val => updateSection('owner', 'loan', { ...scenario.owner.loan, currency: val as any })"
          tooltip="Currency of the lump-sum loan; converted using FX if EUR."
        />
      </div>
    </details>

    <div class="card" style="background: #f8fafc; margin-top: 1rem">
      <div class="label">Import scenario JSON</div>
      <textarea v-model="importText" rows="4" class="text-area" placeholder='{"general": ...}'></textarea>
      <div class="toolbar">
        <button class="primary" @click="importScenario">Import</button>
        <span v-if="importError" class="warning">{{ importError }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.text-area {
  width: 100%;
  margin-top: 0.35rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.6rem;
  font-family: 'Inter', sans-serif;
}
</style>
